import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  useDeferredValue,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";

import { hasPermission } from "../entities/access";
import {
  compareNames,
  descendantFolderIds,
  folderPathLabel,
  indexFolders,
  type FileFolder,
  type FileRecord,
} from "../entities/files";
import { shellBootstrapQuery } from "./bootstrap";
import { FilePreviewDialog } from "./FilePreviewDialog";
import {
  deleteMessage,
  fileMessage,
  folderAccessMessage,
  folderMessage,
  loadFailureReason,
  moveMessage,
  renameMessage,
  type FilesSearch,
} from "./filesSupport";
import { FilesTable } from "./FilesTable";
import { uploadMime } from "./formatting";
import {
  FileMoveDialog,
  FileRenameDialog,
  FolderAccessDialog,
  FolderMoveDialog,
  FolderNameDialog,
  type FolderOption,
  type ScopeOption,
} from "./FolderDialogs";
import { FolderSummary } from "./FolderSummary";
import { FolderTreePanel } from "./FolderTree";
import type { ShellServices } from "./services";
import { EmptyState } from "./states";
import { Notice } from "./Notice";

/** Надпись на кнопке во время загрузки: доля показывается, только когда известна. */
function uploadLabel(progress: number | null): string {
  if (progress === null || progress >= 1) return "Загружаем и проверяем…";
  return `Загружаем… ${Math.round(progress * 100)}%`;
}

function download(url: string, filename: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

type Notice = { tone: "success" | "error"; text: string } | null;

type DialogState =
  | { kind: "create" }
  | { kind: "rename"; folder: FileFolder }
  | { kind: "move"; folder: FileFolder }
  | { kind: "access"; folder: FileFolder }
  | { kind: "moveFile"; file: FileRecord }
  | { kind: "renameFile"; file: FileRecord }
  | null;

type FolderChange =
  | { action: "create"; name: string; parentId: string | null }
  | { action: "rename"; id: string; name: string }
  | { action: "move"; id: string; parentId: string | null };

/**
 * Папки, в которые можно положить файл или другую папку.
 *
 * Только те, что человек вправе менять и что лежат в нужной организации:
 * чужая папка в выбор не попадает, чтобы отказ не приходил после нажатия.
 */
function folderOptions(
  folders: readonly FileFolder[],
  byId: ReadonlyMap<string, FileFolder>,
  scopeId: string | null,
  exclude: ReadonlySet<string> = new Set(),
): FolderOption[] {
  return folders
    .filter(
      (folder) =>
        folder.can_manage &&
        !exclude.has(folder.id) &&
        (scopeId === null || folder.scope_id === scopeId),
    )
    .map((folder) => ({
      id: folder.id,
      label: folderPathLabel(byId, folder.id),
    }))
    .toSorted((left, right) => compareNames(left.label, right.label));
}

/**
 * Где можно создать новую папку.
 *
 * С выбранной организацией родителем годится любая видимая папка, и чужая
 * тоже, если её открыли этой организации: внутри неё заводят свои папки, а
 * принадлежат они создавшей организации. В режиме «Все организации» создать
 * ничего нельзя, поэтому там остаются только управляемые папки.
 */
function createParentOptions(
  folders: readonly FileFolder[],
  byId: ReadonlyMap<string, FileFolder>,
  scopeId: string | null,
): FolderOption[] {
  if (scopeId === null) return folderOptions(folders, byId, null);

  return folders
    .map((folder) => ({
      id: folder.id,
      label: folderPathLabel(byId, folder.id),
    }))
    .toSorted((left, right) => compareNames(left.label, right.label));
}

/**
 * Страница внутри маршрута.
 *
 * Выбранная папка живёт в адресе: ссылку на папку можно переслать, а выбор
 * переживает перезагрузку. Сама страница про адрес ничего не знает и получает
 * выбор пропсами.
 */
export function FilesRoute({ services }: { services: ShellServices }) {
  const search: FilesSearch = useSearch({
    from: "/authenticated/administration/files",
  });
  const navigate = useNavigate({ from: "/administration/files" });

  return (
    <FilesAdminPage
      services={services}
      folderId={search.folder ?? null}
      onSelectFolder={(value) =>
        void navigate({ search: value ? { folder: value } : {}, replace: true })
      }
    />
  );
}

export function FilesAdminPage({
  services,
  folderId,
  onSelectFolder,
}: {
  services: ShellServices;
  folderId?: string | null;
  onSelectFolder?: (value: string | null) => void;
}) {
  const client = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [previewFile, setPreviewFile] = useState<FileRecord | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [search, setSearch] = useState("");
  const [localFolder, setLocalFolder] = useState<string | null>(null);
  // Крупный файл грузится минутами: без доли выполненного окно выглядит
  // зависшим, и человек уходит с середины загрузки.
  const [progress, setProgress] = useState<number | null>(null);
  const selectedId = folderId !== undefined ? folderId : localFolder;
  const selectFolder = onSelectFolder ?? setLocalFolder;
  const deferredSearch = useDeferredValue(search.trim());

  const bootstrap = useQuery(shellBootstrapQuery(services));
  const permissions = bootstrap.data?.permissions ?? [];
  const canDownload = hasPermission({ permissions }, "files.download");
  const canDelete = hasPermission({ permissions }, "files.delete");
  const canManageFolders = hasPermission(
    { permissions },
    "files.folders.manage",
  );
  const currentScope = bootstrap.data?.currentScope ?? null;
  const allOrganizations = bootstrap.data?.allOrganizations ?? false;

  // Список и папки (вместе с доступом КПО) лежат под своими ключами: после
  // мутации обновляются именно они. Ключ ["files"] целиком не трогаем — под ним
  // лежат и подписанные ссылки на скачивание, которые незачем выдавать заново.
  const folderTree = useQuery({
    queryKey: ["files", "folders", "all"],
    queryFn: () => services.files.folders!(),
    retry: false,
  });
  const folderSearch = useQuery({
    queryKey: ["files", "folders", "search", deferredSearch],
    queryFn: () => services.files.folders!(deferredSearch),
    enabled: deferredSearch !== "",
    retry: false,
  });
  const folders = useMemo(() => folderTree.data?.data ?? [], [folderTree.data]);
  const byId = useMemo(() => indexFolders(folders), [folders]);
  const selectedFolder = selectedId ? (byId.get(selectedId) ?? null) : null;
  // Папка из адреса может уже не существовать или быть закрыта: список по ней
  // сервер отвергнет, поэтому не спрашиваем и говорим об этом прямо.
  const selectedMissing =
    selectedId !== null && folderTree.isSuccess && selectedFolder === null;

  const fileList = useQuery({
    queryKey: ["files", "list", selectedId ?? "all"],
    queryFn: () =>
      services.files.list!({
        per_page: 100,
        ...(selectedId ? { folder_id: selectedId } : {}),
      }),
    enabled: !selectedMissing,
    retry: false,
  });
  const files = fileList.data?.data ?? [];

  const needScopes = allOrganizations || dialog?.kind === "access";
  const scopes = useQuery({
    queryKey: ["scopes", "available"],
    queryFn: () => services.administration.listScopes(),
    enabled: needScopes,
    retry: false,
  });
  const scopeNames = useMemo(
    () => new Map((scopes.data ?? []).map((scope) => [scope.id, scope.name])),
    [scopes.data],
  );

  // Доступ читается только там, где его можно менять: соседнему КПО список
  // чужих выдач не нужен, а лишний запрос дал бы 403 в консоли.
  const accessFolderId = selectedFolder?.can_manage ? selectedFolder.id : null;
  const granted = useQuery({
    queryKey: ["files", "folders", "scopes", accessFolderId],
    queryFn: () => services.files.folderScopes!(accessFolderId!),
    enabled: accessFolderId !== null,
    retry: false,
  });

  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["files", "list"] }),
      client.invalidateQueries({ queryKey: ["files", "folders"] }),
    ]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const initiated = await services.files.initiate({
        purpose: "temporary",
        filename: file.name,
        mime_type: uploadMime(file),
        size: file.size,
      });
      setProgress(0);
      await services.files.uploadObject(initiated.upload, file, setProgress);
      return services.files.confirm(initiated.data.id);
    },
    onSuccess: async () => {
      setProgress(null);
      await refresh();
      // Загрузка кладёт файл в корень: в списке открытой папки он не появится,
      // и без пояснения кажется, что файл потерялся.
      setNotice({
        tone: "success",
        text: selectedId
          ? "Файл подтверждён и появился в хранилище — в корне, вне выбранной папки. Откройте «Все файлы» и переместите его в нужную папку."
          : "Файл подтверждён и появился в хранилище.",
      });
    },
    onError: (error) => {
      setProgress(null);
      setNotice({ tone: "error", text: fileMessage(error) });
    },
  });
  const remove = useMutation({
    mutationFn: (file: FileRecord) => services.files.delete!(file.id),
    onSuccess: async () => {
      await refresh();
      setNotice({
        tone: "success",
        text: "Метаданные файла удалены. Байты остаются под политикой хранения сервера.",
      });
    },
    // Кнопка скрыта по can_delete; отказ возможен, если права изменились
    // между загрузкой списка и нажатием, или файл успел стать чьей-то ссылкой.
    onError: async (error, file) => {
      setNotice({ tone: "error", text: deleteMessage(error, file) });
      await refresh();
    },
  });
  const fetchDownload = useMutation({
    mutationFn: (file: FileRecord) => services.files.download(file.id),
    onSuccess: ({ data }, file) => download(data.url, file.filename),
    onError: (error) => setNotice({ tone: "error", text: fileMessage(error) }),
  });

  const folderChange = useMutation({
    mutationFn: (change: FolderChange) => {
      if (change.action === "create") {
        return services.files.createFolder!({
          name: change.name,
          ...(change.parentId ? { parent_id: change.parentId } : {}),
        });
      }
      return services.files.updateFolder!(
        change.id,
        change.action === "rename"
          ? { name: change.name }
          : { parent_id: change.parentId },
      );
    },
    onSuccess: async ({ data }, change) => {
      await refresh();
      setDialog(null);
      setNotice({
        tone: "success",
        text: {
          create: `Папка «${data.name}» создана.`,
          rename: `Папка переименована: «${data.name}».`,
          move: `Папка «${data.name}» перенесена.`,
        }[change.action],
      });
      if (change.action === "create") selectFolder(data.id);
    },
  });
  const accessChange = useMutation({
    mutationFn: ({
      folder,
      scopeIds,
    }: {
      folder: FileFolder;
      scopeIds: string[];
    }) => services.files.replaceFolderScopes!(folder.id, scopeIds),
    onSuccess: async (_result, { folder }) => {
      await refresh();
      setDialog(null);
      setNotice({
        tone: "success",
        text: `Доступ КПО к папке «${folder.name}» сохранён.`,
      });
    },
  });
  const fileMove = useMutation({
    mutationFn: ({
      file,
      folderId: target,
    }: {
      file: FileRecord;
      folderId: string | null;
    }) => services.files.move!(file.id, target),
    onSuccess: async ({ data }, { file }) => {
      await refresh();
      setDialog(null);
      const adopted =
        file.scope_id === null && data.scope_id !== null
          ? " Он привязан к организации папки и виден тем КПО, которым она открыта."
          : "";
      setNotice({
        tone: "success",
        text: data.folder_name
          ? `Файл «${data.original_name ?? data.filename}» перемещён в папку «${data.folder_name}».${adopted}`
          : `Файл «${data.original_name ?? data.filename}» возвращён в корень.`,
      });
    },
  });

  const fileRename = useMutation({
    mutationFn: ({ file, filename }: { file: FileRecord; filename: string }) =>
      services.files.rename!(file.id, filename),
    onSuccess: async ({ data }) => {
      await client.invalidateQueries({ queryKey: ["files", "list"] });
      setDialog(null);
      setNotice({
        tone: "success",
        text: `Файл переименован: «${data.original_name ?? data.filename}».`,
      });
    },
  });

  const openDialog = (next: DialogState) => {
    folderChange.reset();
    accessChange.reset();
    fileMove.reset();
    fileRename.reset();
    setNotice(null);
    setDialog(next);
  };
  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      setNotice(null);
      upload.mutate(file);
    }
  };

  // Файл можно переложить там, где у человека есть управляемая папка той же
  // организации: сам файл флага can_manage не несёт.
  const manageableScopes = useMemo(
    () =>
      new Set(
        folders.flatMap((folder) =>
          folder.can_manage && folder.scope_id ? [folder.scope_id] : [],
        ),
      ),
    [folders],
  );
  // Файлу без организации (загрузка старше организаций) подходит любая
  // управляемая папка: он получит организацию той, в которую его переложат.
  const canMove = (file: FileRecord) =>
    file.scope_id === null
      ? manageableScopes.size > 0
      : manageableScopes.has(file.scope_id);

  // Перетаскивание: что несут сейчас. Допустимость цели считает страница, а
  // не дерево: только она знает организацию файла и вложенность папок.
  const [drag, setDrag] = useState<
    | { kind: "file"; file: FileRecord }
    | { kind: "folder"; folder: FileFolder }
    | null
  >(null);
  const dragDescendants = useMemo(
    () =>
      drag?.kind === "folder"
        ? descendantFolderIds(folders, drag.folder.id)
        : new Set<string>(),
    [drag, folders],
  );
  const moving = fileMove.isPending || folderChange.isPending;
  const canDropOn = (targetId: string | null): boolean => {
    if (drag === null || moving) return false;
    const target = targetId === null ? null : (byId.get(targetId) ?? null);
    if (targetId !== null && target === null) return false;
    if (drag.kind === "file") {
      const { file } = drag;
      // Файл без организации в корень не возвращается: там ему нечем быть.
      if (target === null)
        return file.scope_id !== null && file.folder_id !== null;
      return (
        target.can_manage &&
        (file.scope_id === null || target.scope_id === file.scope_id) &&
        target.id !== file.folder_id
      );
    }
    const { folder } = drag;
    if (target === null) return folder.parent_id !== null;
    return (
      target.can_manage &&
      target.scope_id === folder.scope_id &&
      target.id !== folder.id &&
      target.id !== folder.parent_id &&
      !dragDescendants.has(target.id)
    );
  };
  const dropOn = (targetId: string | null) => {
    const current = drag;
    setDrag(null);
    if (current === null || !canDropOn(targetId)) return;
    setNotice(null);
    if (current.kind === "file") {
      fileMove.mutate(
        { file: current.file, folderId: targetId },
        {
          onError: (error) =>
            setNotice({ tone: "error", text: moveMessage(error) }),
        },
      );
      return;
    }
    folderChange.mutate(
      { action: "move", id: current.folder.id, parentId: targetId },
      {
        onError: (error) =>
          setNotice({ tone: "error", text: folderMessage(error, "move") }),
      },
    );
  };
  // Файлу без организации объясняем, что с ним будет при переносе: иначе
  // организация «появляется» сама, и это выглядит как побочный эффект.
  const moveNote = (file: FileRecord): string | null =>
    file.status === "ready" && file.scope_id === null && canMove(file)
      ? "Без организации: получит её от папки, в которую его переложат."
      : null;

  const badge = (folder: FileFolder): string | null => {
    if (currentScope !== null && folder.scope_id !== currentScope.id)
      return "Открыта вам";
    if (allOrganizations && folder.scope_id)
      return scopeNames.get(folder.scope_id) ?? null;
    return null;
  };

  const ownerName = (folder: FileFolder): string | null =>
    folder.scope_id
      ? (scopeNames.get(folder.scope_id) ??
        (folder.scope_id === currentScope?.id ? currentScope.name : null))
      : null;
  const scopeOptions = (folder: FileFolder): ScopeOption[] =>
    (scopes.data ?? [])
      .filter(
        (scope) =>
          scope.type === "kpo" &&
          scope.is_active &&
          scope.id !== folder.scope_id,
      )
      .map((scope) => ({ id: scope.id, name: scope.name, code: scope.code }))
      .toSorted((left, right) => compareNames(left.name, right.name));

  const foundFolders =
    deferredSearch === "" ? null : (folderSearch.data?.data ?? null);
  const treeStatus = folderTree.isPending
    ? "loading"
    : folderTree.isError
      ? "error"
      : "ready";
  const readOnlyReason =
    selectedFolder &&
    currentScope !== null &&
    selectedFolder.scope_id !== currentScope.id
      ? "Папка открыта вам на чтение: изменять её может только организация-владелец. Внутри неё можно создавать свои папки."
      : "Только чтение: для изменения папок нужно право «Управление папками файлов».";

  // Варианты нужны только открытому окну создания, а не каждой перерисовке.
  const createParents =
    dialog?.kind === "create"
      ? createParentOptions(folders, byId, currentScope?.id ?? null)
      : [];
  const dialogFolderError =
    folderChange.isError && folderChange.variables
      ? folderMessage(folderChange.error, folderChange.variables.action)
      : null;
  const grantedReady = granted.data && scopes.data ? granted.data.data : null;

  return (
    <main className="content-page files-page" aria-labelledby="files-title">
      <header className="page-header files-page-header">
        <div>
          <p className="eyebrow">Приватное хранилище</p>
          <h1 id="files-title">Файлы</h1>
        </div>
        <button
          className="button button--primary"
          type="button"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {upload.isPending ? uploadLabel(progress) : "Загрузить файл"}
        </button>
        <input
          ref={inputRef}
          className="visually-hidden"
          aria-label="Загрузить файл"
          type="file"
          onChange={selectFile}
        />
      </header>
      {notice ? (
        <p
          className={`operation-notice operation-notice--${notice.tone}`}
          role={notice.tone === "error" ? "alert" : "status"}
        >
          {notice.text}
        </p>
      ) : null}
      <div className="files-layout">
        <FolderTreePanel
          folders={folders}
          found={foundFolders}
          searchError={
            deferredSearch !== "" && folderSearch.isError
              ? loadFailureReason(folderSearch.error)
              : null
          }
          status={treeStatus}
          errorReason={
            folderTree.isError ? loadFailureReason(folderTree.error) : null
          }
          search={search}
          onSearch={setSearch}
          selectedId={selectedId}
          onSelect={selectFolder}
          badge={badge}
          onCreate={
            canManageFolders ? () => openDialog({ kind: "create" }) : null
          }
          hint={
            allOrganizations
              ? "Режим «Все организации»: чтобы создать папку или переложить файл, выберите организацию в шапке."
              : null
          }
          onRetry={() => void folderTree.refetch()}
          dnd={
            canManageFolders
              ? {
                  active: drag !== null,
                  draggingFolderId:
                    drag?.kind === "folder" ? drag.folder.id : null,
                  draggableFolder: (folder) => folder.can_manage && !moving,
                  canDrop: canDropOn,
                  onDragStartFolder: (folder) =>
                    setDrag({ kind: "folder", folder }),
                  onDrop: dropOn,
                  onDragEnd: () => setDrag(null),
                }
              : undefined
          }
        />
        <section className="files-main" aria-label="Список файлов">
          {selectedFolder ? (
            <FolderSummary
              folder={selectedFolder}
              byId={byId}
              granted={granted.data?.data ?? null}
              grantedError={
                granted.isError ? loadFailureReason(granted.error) : null
              }
              readOnlyReason={readOnlyReason}
              onOpenFolder={selectFolder}
              onRename={() =>
                openDialog({ kind: "rename", folder: selectedFolder })
              }
              onMove={() =>
                openDialog({ kind: "move", folder: selectedFolder })
              }
              onAccess={() =>
                openDialog({ kind: "access", folder: selectedFolder })
              }
              onCreateInside={
                canManageFolders ? () => openDialog({ kind: "create" }) : null
              }
            />
          ) : null}
          {selectedMissing ? (
            <EmptyState
              title="Этой папки нет среди доступных"
              description="Её могли удалить, переместить в другую организацию или закрыть доступ. Откройте общий список — файлы на месте."
              action={{
                label: "Показать все файлы",
                onClick: () => selectFolder(null),
              }}
            />
          ) : null}
          {!selectedMissing && fileList.isLoading ? (
            <p aria-busy="true">Загружаем хранилище…</p>
          ) : null}
          {!selectedMissing && fileList.isError ? (
            <Notice>
              Не удалось загрузить хранилище:{" "}
              {loadFailureReason(fileList.error)}. Файлы не удалены — повторите
              попытку.
            </Notice>
          ) : null}
          {!selectedMissing &&
          !fileList.isLoading &&
          !fileList.isError &&
          files.length === 0 ? (
            selectedFolder ? (
              <EmptyState
                title="В этой папке пока нет файлов"
                description={[
                  folders.some(
                    (folder) => folder.parent_id === selectedFolder.id,
                  )
                    ? "Файлы вложенных папок показываются в самих вложенных папках."
                    : null,
                  selectedFolder.can_manage
                    ? "Откройте «Все файлы» и переместите сюда нужные файлы кнопкой «Переместить»."
                    : "Файлы, которые положат в эту папку, появятся здесь.",
                ]
                  .filter(Boolean)
                  .join(" ")}
              />
            ) : (
              <EmptyState
                title="В хранилище пока нет файлов"
                description="Загруженные файлы сохраняются и будут показаны здесь после обновления страницы."
              />
            )
          ) : null}
          {!selectedMissing && files.length > 0 ? (
            <FilesTable
              files={files}
              canDownload={canDownload}
              canDelete={canDelete}
              canMove={canMove}
              isKnownFolder={(id) => byId.has(id)}
              busy={{
                download: fetchDownload.isPending,
                remove: remove.isPending,
              }}
              onOpen={setPreviewFile}
              onDownload={(file) => fetchDownload.mutate(file)}
              onDelete={(file) => {
                if (
                  window.confirm(
                    `Удалить метаданные файла «${file.original_name ?? file.filename}»?`,
                  )
                )
                  remove.mutate(file);
              }}
              onMove={(file) => openDialog({ kind: "moveFile", file })}
              onRename={(file) => openDialog({ kind: "renameFile", file })}
              onOpenFolder={selectFolder}
              moveNote={moveNote}
              draggingFileId={drag?.kind === "file" ? drag.file.id : null}
              onDragStartFile={(file) => setDrag({ kind: "file", file })}
              onDragEnd={() => setDrag(null)}
            />
          ) : null}
        </section>
      </div>
      {previewFile ? (
        <FilePreviewDialog
          file={previewFile}
          services={services}
          onClose={() => setPreviewFile(null)}
        />
      ) : null}
      {dialog?.kind === "create" ? (
        <FolderNameDialog
          mode="create"
          parents={createParents}
          defaultParentId={
            selectedFolder &&
            createParents.some((option) => option.id === selectedFolder.id)
              ? selectedFolder.id
              : null
          }
          pending={folderChange.isPending}
          error={dialogFolderError}
          onSubmit={({ name, parentId }) =>
            folderChange.mutate({ action: "create", name, parentId })
          }
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "rename" ? (
        <FolderNameDialog
          mode="rename"
          folder={dialog.folder}
          parents={[]}
          defaultParentId={null}
          pending={folderChange.isPending}
          error={dialogFolderError}
          onSubmit={({ name }) =>
            folderChange.mutate({
              action: "rename",
              id: dialog.folder.id,
              name,
            })
          }
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "move" ? (
        <FolderMoveDialog
          folder={dialog.folder}
          parents={folderOptions(
            folders,
            byId,
            dialog.folder.scope_id,
            new Set([
              dialog.folder.id,
              ...descendantFolderIds(folders, dialog.folder.id),
            ]),
          )}
          pending={folderChange.isPending}
          error={dialogFolderError}
          onSubmit={(parentId) =>
            folderChange.mutate({
              action: "move",
              id: dialog.folder.id,
              parentId,
            })
          }
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "access" ? (
        <FolderAccessDialog
          folder={dialog.folder}
          ownerName={ownerName(dialog.folder)}
          options={scopeOptions(dialog.folder)}
          granted={grantedReady}
          loading={granted.isPending || scopes.isPending}
          loadError={
            granted.isError || scopes.isError
              ? loadFailureReason(granted.error ?? scopes.error)
              : null
          }
          pending={accessChange.isPending}
          error={
            accessChange.isError
              ? folderAccessMessage(accessChange.error)
              : null
          }
          onRetry={() => {
            void granted.refetch();
            void scopes.refetch();
          }}
          onSubmit={(scopeIds) =>
            accessChange.mutate({ folder: dialog.folder, scopeIds })
          }
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "moveFile" ? (
        <FileMoveDialog
          file={dialog.file}
          folders={folderOptions(folders, byId, dialog.file.scope_id)}
          pending={fileMove.isPending}
          error={fileMove.isError ? moveMessage(fileMove.error) : null}
          onSubmit={(target) =>
            fileMove.mutate({ file: dialog.file, folderId: target })
          }
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "renameFile" ? (
        <FileRenameDialog
          filename={dialog.file.original_name ?? dialog.file.filename}
          pending={fileRename.isPending}
          error={fileRename.isError ? renameMessage(fileRename.error) : null}
          onSubmit={(filename) =>
            fileRename.mutate({ file: dialog.file, filename })
          }
          onClose={() => {
            if (!fileRename.isPending) setDialog(null);
          }}
        />
      ) : null}
    </main>
  );
}
